import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Grid, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from "@mui/material";

import useOffers from "../../hooks/useOffers";
import logger from "../../utils/logger";
import { OFFER_STATUS, getOfferStats, offerStatusLabel } from "../../utils/offers";

const StatCard = ({ label, value, note }) => (
  <Grid item xs={12} sm={6} md={3}>
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h4" fontWeight={700} sx={{ my: 0.5 }}>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{note}</Typography>
      </CardContent>
    </Card>
  </Grid>
);

/** Newest activity first. */
const byLatest = (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);

/**
 * Manager's Offers page (/offers): status of every offer in the tenant,
 * read-only. Offers waiting for approval link to Approvals, where the manager
 * decides; every other offer opens its detail page.
 */
const Offers = () => {
  const navigate = useNavigate();
  const { data: offers = [], isLoading, error } = useOffers();

  const stats = useMemo(() => getOfferStats(offers), [offers]);
  const rows = useMemo(() => [...offers].sort(byLatest), [offers]);

  useEffect(() => {
    if (error) logger.error(`[Offers] Failed to load offers — ${error?.response?.data?.error || error.message}`);
  }, [error]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>Offers</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Status of every offer in your team. Offers waiting for your decision are in Approvals.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <StatCard label="Sent" value={stats.sent} note="Emails delivered" />
        <StatCard label="Accepted" value={stats.accepted} note={`of ${stats.sent} sent`} />
        <StatCard label="Acceptance rate" value={`${stats.acceptanceRate}%`} note="Accepted / sent" />
        <StatCard
          label="Avg. time to accept"
          value={stats.avgTimeToAccept}
          note={`${stats.acceptedWithTimes} accepted offer${stats.acceptedWithTimes === 1 ? "" : "s"}`}
        />
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }}>Could not load offers. Please try again.</Alert>}

      <Paper variant="outlined">
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ textAlign: "center", p: 6 }}>
            <Typography variant="h6" fontWeight={600}>No offers yet</Typography>
            <Typography variant="body2" color="text.secondary">Offers appear here when candidates reach the offer stage.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Candidate</TableCell>
                  <TableCell>Job</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((offer) => {
                  const pending = offer.status === OFFER_STATUS.PENDING_APPROVAL;
                  return (
                    <TableRow key={offer.offer_id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{offer.profile?.candidate_name || "Candidate"}</Typography>
                      </TableCell>
                      <TableCell>{[offer.job?.job_code, offer.job?.title].filter(Boolean).join(" ") || "—"}</TableCell>
                      <TableCell>{offerStatusLabel(offer)}</TableCell>
                      <TableCell align="right">
                        {pending ? (
                          <Button size="small" variant="contained" onClick={() => navigate("/approvals")}>
                            Review in Approvals
                          </Button>
                        ) : (
                          <Button size="small" variant="outlined" onClick={() => navigate(`/offers/${offer.offer_id}`)}>
                            Open
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default Offers;
